import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FiX, FiTrash2 } from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';
import useFavoritePages from '../hooks/useFavoritePages';
import { routeIconForPath, routeLabels } from '../constants/routes';
import { t } from '../i18n';

const COLOR_PRESETS = [
    { name: 'Default', value: null, swatch: '#6b7280' },
    { name: 'Red', value: '#dc2626', swatch: '#dc2626' },
    { name: 'Orange', value: '#ea580c', swatch: '#ea580c' },
    { name: 'Green', value: '#16a34a', swatch: '#16a34a' },
    { name: 'Blue', value: '#2563eb', swatch: '#2563eb' },
    { name: 'Purple', value: '#9333ea', swatch: '#9333ea' },
    { name: 'Gray', value: '#6b7280', swatch: '#9ca3af' },
];

const ColorPopover = ({ anchorRect, currentColor, onPick, onClose }) => {
    const ref = useRef(null);
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    if (!anchorRect) return null;

    const popoverWidth = 220;
    const left = Math.max(8, Math.min(window.innerWidth - popoverWidth - 8, anchorRect.right - popoverWidth));
    const top = anchorRect.bottom + 4;

    return createPortal(
        <div
            ref={ref}
            className="fixed z-[10001] bg-white border border-gray-200 rounded shadow-lg p-2 flex items-center gap-1"
            style={{ top, left, width: popoverWidth }}
            onClick={(e) => e.stopPropagation()}
        >
            {COLOR_PRESETS.map(preset => {
                const isActive = (preset.value ?? null) === (currentColor ?? null);
                return (
                    <button
                        key={preset.name}
                        type="button"
                        onClick={() => { onPick(preset.value); onClose(); }}
                        className={`rounded-full border-2 flex items-center justify-center flex-shrink-0 ${isActive ? 'border-gray-800' : 'border-transparent hover:border-gray-300'}`}
                        style={{ width: '24px', height: '24px', backgroundColor: preset.swatch, padding: 0 }}
                        title={preset.name}
                    >
                        {preset.value === null && (
                            <span className="text-[9px] text-white font-bold leading-none">A</span>
                        )}
                    </button>
                );
            })}
        </div>,
        document.body
    );
};

const SortableRow = ({ fav, onRename, onToggleBold, onSetColor, onRemove }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: fav.path });
    const [localLabel, setLocalLabel] = useState(fav.title ?? '');
    const [colorAnchor, setColorAnchor] = useState(null);
    const colorBtnRef = useRef(null);
    const IconComponent = routeIconForPath(fav.path);
    const defaultLabel = t(routeLabels[fav.path] || fav.path);

    useEffect(() => { setLocalLabel(fav.title ?? ''); }, [fav.title]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const commitLabel = () => {
        if ((localLabel ?? '') !== (fav.title ?? '')) {
            onRename(fav.path, localLabel);
        }
    };

    const previewStyle = {};
    if (fav.color) previewStyle.color = fav.color;
    if (fav.bold) previewStyle.fontWeight = 700;

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center gap-2 py-2 px-2 border-b border-gray-100 bg-white"
        >
            <button
                type="button"
                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-700 flex-shrink-0"
                {...attributes}
                {...listeners}
                title={t('Drag to reorder')}
            >
                <MdDragIndicator className="text-lg" />
            </button>

            {IconComponent && (
                <IconComponent className="text-sm text-gray-500 flex-shrink-0" style={fav.color ? { color: fav.color } : undefined} />
            )}

            <input
                type="text"
                value={localLabel}
                onChange={(e) => setLocalLabel(e.target.value)}
                onBlur={commitLabel}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                placeholder={defaultLabel}
                className="flex-1 min-w-0 px-2 py-1 text-sm border border-transparent focus:border-gray-300 focus:outline-none rounded"
                style={previewStyle}
            />

            <button
                type="button"
                onClick={() => onToggleBold(fav.path)}
                className={`w-7 h-7 flex items-center justify-center rounded text-xs font-bold flex-shrink-0 ${fav.bold ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                title={t('Bold')}
            >
                B
            </button>

            <button
                ref={colorBtnRef}
                type="button"
                onClick={() => {
                    if (colorAnchor) {
                        setColorAnchor(null);
                    } else if (colorBtnRef.current) {
                        setColorAnchor(colorBtnRef.current.getBoundingClientRect());
                    }
                }}
                className="w-7 h-7 flex items-center justify-center rounded border border-gray-200 hover:border-gray-400 flex-shrink-0"
                title={t('Color')}
            >
                <span
                    className="w-4 h-4 rounded-full border border-gray-300"
                    style={{ backgroundColor: fav.color || '#f3f4f6' }}
                />
            </button>
            {colorAnchor && (
                <ColorPopover
                    anchorRect={colorAnchor}
                    currentColor={fav.color}
                    onPick={(c) => onSetColor(fav.path, c)}
                    onClose={() => setColorAnchor(null)}
                />
            )}

            <button
                type="button"
                onClick={() => onRemove(fav.path)}
                className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:bg-red-50 hover:text-red-600 flex-shrink-0"
                title={t('Remove')}
            >
                <FiTrash2 className="text-sm" />
            </button>
        </div>
    );
};

const FavoritesManagerPanel = ({ open, onClose }) => {
    const { favorites, updateLabel, toggleBold, setColor, removeFavorite, reorder, max } = useFavoritePages();

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;

    const handleDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const fromIndex = favorites.findIndex(f => f.path === active.id);
        const toIndex = favorites.findIndex(f => f.path === over.id);
        if (fromIndex < 0 || toIndex < 0) return;
        reorder(fromIndex, toIndex);
    };

    const ids = favorites.map(f => f.path);

    return createPortal(
        <div className="fixed inset-0 z-[9999]">
            <div
                className="absolute inset-0 bg-black/30"
                onClick={onClose}
            />
            <div className="absolute top-0 right-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">{t('Manage featured pages')}</h2>
                        <p className="text-xs text-gray-500">{favorites.length} / {max} — drag to reorder</p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="w-8 h-8 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100"
                        title={t('Close')}
                    >
                        <FiX />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {favorites.length === 0 ? (
                        <div className="p-6 text-center text-sm text-gray-500">
                            {t('No featured pages yet. Star pages from the sidebar.')}
                        </div>
                    ) : (
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                                {favorites.map(fav => (
                                    <SortableRow
                                        key={fav.path}
                                        fav={fav}
                                        onRename={updateLabel}
                                        onToggleBold={toggleBold}
                                        onSetColor={setColor}
                                        onRemove={removeFavorite}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default FavoritesManagerPanel;
